// Social publishing connectors: Meta (Facebook Pages + Instagram) and Threads have real OAuth clients;
// YouTube and TikTok are listed as known-but-not-yet-configured connectors (no API credentials wired up
// yet) so they appear honestly in the UI instead of being silently absent. A connector can be collegato
// per workspace, or once at the company level (scope 'organization') and then assigned to workspaces
// (see WorkspaceChannel below) - the same pattern Google Calendar already uses for a shared calendar.
import { z } from 'zod';

const Uuid = z.string().uuid();

export const SOCIAL_CONNECTOR_KEYS = ['meta', 'threads', 'youtube', 'tiktok'] as const;
export type SocialConnectorKey = (typeof SOCIAL_CONNECTOR_KEYS)[number];

/** Content-facing platforms - Meta's single OAuth connector splits into two here because Facebook
 * Pages and Instagram accounts are shown, filtered and assigned separately. */
export const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'threads', 'youtube', 'tiktok'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const SocialAuthorizeInput = z.object({
  scope: z.enum(['workspace', 'organization']).default('workspace'),
  workspaceId: Uuid.optional(),
}).refine((v) => v.scope === 'organization' || Boolean(v.workspaceId), { message: 'workspaceId è obbligatorio per un collegamento di spazio.' });
export type SocialAuthorizeInput = z.infer<typeof SocialAuthorizeInput>;

export const UpdateSocialConnectionInput = z.object({ selectedPageIds: z.array(z.string().min(1).max(100)).max(50) });
export type UpdateSocialConnectionInput = z.infer<typeof UpdateSocialConnectionInput>;

/** A publishable destination: a Facebook Page (optionally with its linked Instagram Business account),
 * or — for Threads — the single connected profile, modeled the same way for a uniform UI. */
export interface SocialPage {
  id: string;
  name: string;
  instagramAccountId: string | null;
  instagramUsername: string | null;
  selected: boolean;
}

export interface SocialConnectionInfo {
  id: string;
  connectorKey: SocialConnectorKey;
  scope: 'workspace' | 'organization';
  /** Null for a company-wide (scope 'organization') connection. */
  workspaceId: string | null;
  workspaceName: string | null;
  accountLabel: string | null;
  status: 'connected' | 'error' | 'expired' | 'disconnected';
  pages: SocialPage[];
  canManage: boolean;
}

export interface SocialConnectorInfo {
  key: SocialConnectorKey;
  name: string;
  description: string;
  /** Whether the server has OAuth client credentials for this connector. */
  configured: boolean;
  connections: SocialConnectionInfo[];
}

// ------------------------------------------------------------------ content labels

export interface ContentLabel {
  id: string;
  workspaceId: string;
  name: string;
  color: string;
}

export const ContentLabelInput = z.object({
  name: z.string().min(1).max(30),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type ContentLabelInput = z.infer<typeof ContentLabelInput>;

export const AssignContentLabelsInput = z.object({ workspaceId: Uuid, labelIds: z.array(Uuid).max(20) });
export type AssignContentLabelsInput = z.infer<typeof AssignContentLabelsInput>;

// ------------------------------------------------------------------ content library

export interface ContentMetrics {
  likes: number | null;
  comments: number | null;
  shares: number | null;
  reach: number | null;
  views: number | null;
}

export interface SocialContentItem {
  /** `${connectionId}:${externalId}` - unique across every workspace/connector. */
  id: string;
  connectionId: string;
  connectorKey: SocialConnectorKey;
  platform: SocialPlatform;
  workspaceId: string;
  externalId: string;
  kind: 'post' | 'reel' | 'story' | 'thread' | 'video';
  caption: string | null;
  permalink: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  publishedAt: string;
  metrics: ContentMetrics;
  labels: ContentLabel[];
}

export interface SocialContentList {
  items: SocialContentItem[];
  /** One line per account Meta/Threads refused to read: shown to the user, never a silently shorter list. */
  unavailable: string[];
}

// ------------------------------------------------------------------ insights

export interface InsightsBreakdown {
  [bucket: string]: number;
}

export interface InsightsSnapshot {
  connectionId: string;
  connectorKey: SocialConnectorKey;
  platform: SocialPlatform;
  accountLabel: string | null;
  pageId: string | null;
  pageName: string;
  fetchedAt: string;
  followers: number | null;
  followersDelta: number | null;
  engagementRate: number | null;
  /** Extra account totals over the last 28 days, labelled in Italian (only what Meta actually returned). */
  stats28d: Array<{ label: string; value: number }>;
  audienceAge: InsightsBreakdown | null;
  audienceGender: InsightsBreakdown | null;
  audienceLocations: InsightsBreakdown | null;
  topContent: SocialContentItem[];
  /** Set when Meta/Threads refused or doesn't yet expose this metric for this account (e.g. below the
   * minimum follower threshold for demographics) - shown to the user verbatim, never silently hidden. */
  unavailable: string[];
}

// ------------------------------------------------------------------ marketing / ads

export interface AdAccountInfo {
  id: string;
  name: string;
  currency: string;
  status: string;
}

export interface Campaign {
  id: string;
  name: string;
  status: string;
  objective: string | null;
  dailyBudget: number | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  currency: string;
}

export interface MarketingInfo {
  connectionId: string;
  /** Null when the current token doesn't carry ads_read/ads_management yet (needs reconnecting). */
  adAccounts: AdAccountInfo[] | null;
}

export const SetCampaignStatusInput = z.object({ workspaceId: Uuid, adAccountId: z.string().min(1).max(100), status: z.enum(['ACTIVE', 'PAUSED']) });
export type SetCampaignStatusInput = z.infer<typeof SetCampaignStatusInput>;

// ------------------------------------------------------------------ company accounts assigned to a workspace
// A company (scope 'organization') connection can list many Pages/IG accounts across the whole Business
// Manager; assigning one to a workspace picks which single one that workspace actually uses, one per
// platform - this is the "un solo account per piattaforma per spazio" rule for company-controlled accounts.

export interface CompanyAccountOption {
  connectionId: string;
  connectorKey: SocialConnectorKey;
  accountLabel: string | null;
  /** Null for Threads (single profile per connection); a Facebook Page id otherwise. */
  pageId: string | null;
  pageName: string;
}

export interface WorkspaceChannelAssignment {
  connectionId: string;
  pageId: string | null;
  pageName: string;
  accountLabel: string | null;
  assignedAt: string;
}

export interface WorkspaceChannel {
  platform: SocialPlatform;
  assignment: WorkspaceChannelAssignment | null;
  /** Company-connected accounts this workspace could be assigned, for this platform. */
  options: CompanyAccountOption[];
}

export const AssignWorkspaceChannelInput = z.object({ connectionId: Uuid, pageId: z.string().min(1).max(100).nullable() });
export type AssignWorkspaceChannelInput = z.infer<typeof AssignWorkspaceChannelInput>;

// ------------------------------------------------------------------ social accounts (Settings → Integrazioni)
// The company-account-driven view of the same assignment: one row per connected Page/Instagram
// account/Threads profile, each showing which single workspace (if any) currently uses it. This is the
// primary way accounts get connected and assigned - see docs on WorkspaceChannel above for the
// workspace-driven read of the same underlying data.

export interface SocialAccount {
  connectionId: string;
  connectorKey: SocialConnectorKey;
  platform: SocialPlatform;
  /** Null for Threads (single profile per connection); a Facebook Page, YouTube channel or TikTok open_id otherwise. */
  pageId: string | null;
  name: string;
  assignedWorkspaceId: string | null;
  assignedWorkspaceName: string | null;
}

export const AssignSocialAccountInput = z.object({
  connectionId: Uuid,
  platform: z.enum(['facebook', 'instagram', 'threads', 'youtube', 'tiktok']),
  pageId: z.string().min(1).max(100).nullable(),
  workspaceId: Uuid.nullable(),
});
export type AssignSocialAccountInput = z.infer<typeof AssignSocialAccountInput>;
